/* eslint react-hooks/exhaustive-deps: "warn" */
import { useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MsvScenarioSwitcher.module.scss';

type Translate = (key: string) => string;

type FieldStatus = 'ok' | 'diff' | 'bad' | 'add';
type VerdictType = 'ok' | 'bad' | 'warn';

type Scenario = {
  left: [string, string][];
  right: [string, string, string, FieldStatus][];
  verdict: { type: VerdictType; label: string; text: string };
  src: string;
};

const getTabs = (t: Translate): { label: string; tone: VerdictType }[] => [
  { label: t('Clean seller'), tone: 'ok' },
  { label: t('Dissolved company'), tone: 'bad' },
  { label: t('Number belongs elsewhere'), tone: 'warn' },
  { label: t('Suspended seller, new shell'), tone: 'bad' },
];

// Left/right table rows and source lines below are specimen verification
// records — left unwrapped by convention. Only the verdict decision and its
// explanatory text are editorial copy and wrapped with t().
const getScenarios = (t: Translate): Scenario[] => [
  {
    left: [
      ['Company name', 'NORDVIK TRADING AB'],
      ['Registration number', '556789-1234'],
      ['Country', 'Sweden'],
      ['Address', 'Kungsgatan 12, Stockholm'],
      ['VAT', 'SE556789123401'],
    ],
    right: [
      ['Legal name', 'Nordvik Trading AB', 'Match', 'ok'],
      ['Registration number', '556789-1234', 'Match', 'ok'],
      ['Status', 'Active since 14 Mar 2019', 'Confirmed', 'ok'],
      ['Registered address', 'Sveavägen 44, Stockholm', 'Differs', 'diff'],
      ['VAT', 'SE556789123401 · valid', 'Match', 'ok'],
      ['Officers', 'A. Lindqvist, M. Fors', 'Added', 'add'],
      ['Beneficial owner', 'A. Lindqvist · 62.5%', 'Added', 'add'],
      ['Officer graph', 'No suspended links', 'Clear', 'ok'],
    ],
    verdict: {
      type: 'ok',
      label: t('Approve'),
      text: t(
        'Trading address legitimately differs from the registered address in most jurisdictions. The mismatch is surfaced, the policy call stays with you.',
      ),
    },
    src: 'Bolagsverket · Swedish Companies Registration Office · synced 26 Jul 2026',
  },
  {
    left: [
      ['Company name', 'HALDEN LOGISTIKK AS'],
      ['Registration number', '9XXXXXXXX'],
      ['Country', 'Norway'],
      ['Address', 'Storgata 8, Oslo'],
      ['VAT', 'NO9XXXXXXXXMVA'],
    ],
    right: [
      ['Legal name', 'Halden Logistikk AS', 'Match', 'ok'],
      ['Registration number', '9XXXXXXXX', 'Match', 'ok'],
      ['Status', 'Dissolved 14 Nov 2025', 'Failed', 'bad'],
      ['Registered address', 'Deregistered', 'Absent', 'bad'],
      ['VAT', 'Deregistered', 'Failed', 'bad'],
      ['Officers', 'Last filing Sep 2025', 'Added', 'add'],
      ['Beneficial owner', 'Not held', 'Absent', 'add'],
      ['Officer graph', 'No suspended links', 'Clear', 'ok'],
    ],
    verdict: {
      type: 'bad',
      label: t('Reject'),
      text: t(
        'The number is real and the name matches. The company stopped existing eight months ago. A regex on the number format passes this seller; a register does not.',
      ),
    },
    src: 'Brønnøysundregistrene · Norway · synced 26 Jul 2026',
  },
  {
    left: [
      ['Company name', 'VERTEX HOME GOODS LTD'],
      ['Registration number', '09XXXXXX'],
      ['Country', 'United Kingdom'],
      ['Address', 'Unit 4, Sheffield'],
      ['VAT', 'not supplied'],
    ],
    right: [
      ['Number resolves to', 'Northgate Fabrication Ltd', 'Mismatch', 'bad'],
      ['Declared name', 'No active match', 'Failed', 'bad'],
      ['Status of resolved entity', 'Active', 'Confirmed', 'add'],
      ['Registered address', 'Leeds, LS1', 'Differs', 'diff'],
      ['Similar names', '2 candidates · confidence 0.44', 'Ambiguous', 'diff'],
      ['Officers', 'Of resolved entity only', 'Added', 'add'],
      ['Beneficial owner', 'Of resolved entity only', 'Added', 'add'],
      ['Officer graph', 'No suspended links', 'Clear', 'ok'],
    ],
    verdict: {
      type: 'warn',
      label: t('Escalate'),
      text: t(
        'The registration number belongs to a different company. Either a transcription error or a borrowed identifier. Both need a human, and the evidence for that conversation is already assembled.',
      ),
    },
    src: 'Companies House · United Kingdom · synced 26 Jul 2026',
  },
  {
    left: [
      ['Company name', 'KOWALSKI HANDEL SP. Z O.O.'],
      ['Registration number', '0000XXXXXX'],
      ['Country', 'Poland'],
      ['Address', 'ul. Prosta 51, Warszawa'],
      ['VAT', 'not yet issued'],
    ],
    right: [
      ['Legal name', 'Kowalski Handel sp. z o.o.', 'Match', 'ok'],
      ['Registration number', '0000XXXXXX', 'Match', 'ok'],
      ['Status', 'Active', 'Confirmed', 'ok'],
      ['Incorporated', '8 Apr 2026', 'Added', 'add'],
      ['VAT', 'Not yet issued', 'Absent', 'diff'],
      ['Officers', 'D. Kowalski', 'Added', 'add'],
      ['Officer graph', 'Atlas Parts Group Ltd · suspended', 'Hit', 'bad'],
      ['Officer graph', 'Velmark Supply OÜ · suspended', 'Hit', 'bad'],
    ],
    verdict: {
      type: 'bad',
      label: t('Reject'),
      text: t(
        'The entity is genuinely clean. It is four months old and has no history to be dirty. The officer behind it is filed against two sellers you already removed.',
      ),
    },
    src: 'Krajowy Rejestr Sądowy · Poland · officer graph across UK, EE, PL · synced 26 Jul 2026',
  },
];

const statusClass = (s: FieldStatus) => {
  if (s === 'ok') return styles.sOk;
  if (s === 'diff') return styles.sDiff;
  if (s === 'bad') return styles.sBad;
  return styles.sAdd;
};

const verdictClass = (t: VerdictType) => {
  if (t === 'ok') return styles.vbOk;
  if (t === 'bad') return styles.vbBad;
  return styles.vbWarn;
};

const verdictTagClass = (t: VerdictType) => {
  if (t === 'ok') return styles.vbTagOk;
  if (t === 'bad') return styles.vbTagBad;
  return styles.vbTagWarn;
};

export const MsvScenarioSwitcher = () => {
  const { t } = useInt();
  const [active, setActive] = useState(0);
  const [fading, setFading] = useState(false);

  const tabs = getTabs(t);
  const scenarios = getScenarios(t);

  const switchTab = (i: number) => {
    if (i === active) return;
    setFading(true);
    setTimeout(() => {
      setActive(i);
      setFading(false);
    }, 200);
  };

  const scenario = scenarios[active];

  return (
    <Section background="#eef1fa" className={styles.root}>
      <TitleBlock title={t('Your form captured a string. Here is what the register holds.')} align="left" />

      <div className={styles.tabs}>
        {tabs.map((tab, i) => (
          <button
            key={tab.label}
            className={cn(styles.tab, {
              [styles.tabOn]: active === i,
              [styles.tabOk]: tab.tone === 'ok',
              [styles.tabBad]: tab.tone === 'bad',
              [styles.tabWarn]: tab.tone === 'warn',
            })}
            onClick={() => switchTab(i)}
          >
            <span className={styles.tabDot} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.stage}>
        <div className={styles.stageLeft}>
          <span className={styles.stageLabel}>{t('Submitted at signup')}</span>
          <div className={cn(styles.stageBody, { [styles.stageFade]: fading })}>
            {scenario.left.map(([k, v]) => (
              <div key={k} className={styles.field}>
                <div className={styles.fieldKey}>{k}</div>
                <div className={styles.fieldVal}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.stageRight}>
          <span className={styles.stageLabel}>{t('Government register')}</span>
          <div className={cn(styles.stageBody, { [styles.stageFade]: fading })}>
            {scenario.right.map(([k, v, s, tone], i) => (
              <div key={`${k}-${i}`} className={styles.rrow}>
                <span className={styles.rKey}>{k}</span>
                <span className={styles.rVal}>{v}</span>
                <span className={cn(styles.rStatus, statusClass(tone))}>{s}</span>
              </div>
            ))}

            <div className={cn(styles.verdictBar, verdictClass(scenario.verdict.type))}>
              <span className={cn(styles.verdictTag, verdictTagClass(scenario.verdict.type))}>
                {scenario.verdict.label}
              </span>
              <p className={styles.verdictText}>{scenario.verdict.text}</p>
            </div>

            <div className={styles.srcLine}>{scenario.src}</div>
          </div>
        </div>
      </div>
    </Section>
  );
};
